import { useNavigate } from 'react-router-dom'
import type { District, RegionType, SettlementType } from '@/types'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { CohortCount } from '@/components/cohort/CohortCount'
import { GenerateCohortsButton } from '@/components/cohort/GenerateCohortsButton'
import { useCohortsByDistrict } from '@/hooks/useCohortsByDistrict'

interface DistrictCardProps {
  district: District
  regionType?: RegionType
}

const settlementTypeLabels: Record<SettlementType, string> = {
  rural: 'Rural',
  suburban: 'Suburban',
  urban: 'Urban',
}

const settlementTypeBadgeStyles: Record<SettlementType, string> = {
  rural: 'bg-green-100 text-green-800 border-green-200',
  suburban: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  urban: 'bg-blue-100 text-blue-800 border-blue-200',
}

function formatPopulation(population: number): string {
  return population.toLocaleString()
}

export function DistrictCard({ district, regionType }: DistrictCardProps) {
  const navigate = useNavigate()
  const { cohorts, isLoading: cohortsLoading } = useCohortsByDistrict(district.id)
  const cohortCount = cohorts?.length

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-lg">{district.name}</CardTitle>
          <Badge
            variant="outline"
            className={cn(settlementTypeBadgeStyles[district.settlement_type])}
          >
            {settlementTypeLabels[district.settlement_type]}
          </Badge>
        </div>
        <CardDescription className="flex items-center gap-2">
          <span>District in this region</span>
          <CohortCount count={cohortCount} isLoading={cohortsLoading} />
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-1 text-sm">
          <div>
            <span className="text-muted-foreground">Population:</span>{' '}
            <span className="font-medium">{formatPopulation(district.population)}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Seats:</span>{' '}
            <span className="font-medium">{district.seat_count}</span>
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex gap-2">
        <Button variant="outline" size="sm" onClick={() => navigate(`/districts/${district.id}`)}>
          View Cohorts
        </Button>
        {regionType && (
          <GenerateCohortsButton
            districtId={district.id}
            districtName={district.name}
            districtPopulation={district.population}
            settlementType={district.settlement_type}
            regionType={regionType}
            variant="default"
          />
        )}
      </CardFooter>
    </Card>
  )
}
